import { Widget, WidgetComponent, WidgetState } from 'app/campaign/widgets.model';

export type VersionedKind = 'component' | 'state';

export interface Versioned {
  id?: number;
  version?: string;
}

const MAX_COPIES = 2000;

// What this tab knows about the versions of the components and steps it loads and saves. The editor keeps its copies
// after its own saves, so a later save of such a copy is sent at the version this tab's last save produced, and only
// a change made somewhere else is refused.
export class WidgetSaveVersions {
  private readonly copies = new Map<string, unknown>();
  // Versions are hashes of the content, so a setting turned back gives an earlier version again: per item, the newest
  // version this tab's saves produced and every version they replaced, rather than a chain from version to version.
  private readonly newest = new Map<string, { version: string; replaced: Set<string> }>();
  private readonly ownVersions = new Set<string>();
  private readonly patched = new Set<string>();

  /** What a save of the item writes: a step's containers are saved on their own. */
  public static ownContent<T extends Versioned>(kind: VersionedKind, item: T): T {
    if (kind !== 'state' || !item) {
      return item;
    }
    const step = { ...item };
    delete step['containers'];
    delete step['floatingContainer'];
    return step;
  }

  public static find(widget: Widget, kind: VersionedKind, id: number): Versioned | undefined {
    for (const state of widget?.states ?? []) {
      if (kind === 'state' && state.id === id) {
        return state;
      }
      for (const container of [
        ...(state.containers ?? []),
        ...(state.floatingContainer ? [state.floatingContainer] : []),
      ]) {
        const component =
          kind === 'component' ? WidgetSaveVersions.findComponent(container.components ?? [], id) : undefined;
        if (component) {
          return component;
        }
      }
    }
    return undefined;
  }

  private static findComponent(components: WidgetComponent[], id: number): WidgetComponent | undefined {
    for (const component of components) {
      const found = component.id === id ? component : WidgetSaveVersions.findComponent(component.children ?? [], id);
      if (found) {
        return found;
      }
    }
    return undefined;
  }

  public rememberWidget(widget: Widget): void {
    for (const state of widget?.states ?? []) {
      this.rememberState(state);
    }
  }

  public rememberState(state: WidgetState): void {
    if (!state) {
      return;
    }
    this.remember('state', state);
    this.loaded('state', state);
    for (const container of [
      ...(state.containers ?? []),
      ...(state.floatingContainer ? [state.floatingContainer] : []),
    ]) {
      for (const component of container.components ?? []) {
        this.rememberComponent(component);
      }
    }
  }

  public rememberComponent(component: WidgetComponent): void {
    if (!component) {
      return;
    }
    this.remember('component', component);
    this.loaded('component', component);
    for (const child of component.children ?? []) {
      this.rememberComponent(child);
    }
  }

  public saved(kind: VersionedKind, sentVersion: string | undefined, saved: Versioned): void {
    if (!saved?.version) {
      return;
    }
    this.remember(kind, saved);
    this.ownVersions.add(this.key(kind, saved.id, saved.version));
    const item = `${kind}:${saved.id}`;
    const newest = this.newest.get(item) ?? { version: saved.version, replaced: new Set<string>() };
    if (sentVersion) {
      newest.replaced.add(sentVersion);
    }
    newest.version = saved.version;
    this.newest.set(item, newest);
  }

  /** A change by this tab whose new version it never sees, such as a patch. */
  public patchedHere(kind: VersionedKind, id: number): void {
    this.patched.add(`${kind}:${id}`);
  }

  /** The version to send for a copy loaded at `version`: the one this tab's own saves since then produced. */
  public current(kind: VersionedKind, id: number, version: string | undefined): string | undefined {
    const newest = this.newest.get(`${kind}:${id}`);
    return version && newest?.replaced.has(version) ? newest.version : version;
  }

  public copy<T>(kind: VersionedKind, id: number, version: string | undefined): T | undefined {
    const copy = version ? this.copies.get(this.key(kind, id, version)) : undefined;
    return copy ? (JSON.parse(JSON.stringify(copy)) as T) : undefined;
  }

  /** True when this tab's own saves or patches explain the version, so nobody else changed it. */
  public madeHere(kind: VersionedKind, id: number, version: string | undefined): boolean {
    const patched = this.patched.delete(`${kind}:${id}`);
    return patched || (!!version && this.ownVersions.has(this.key(kind, id, version)));
  }

  private remember(kind: VersionedKind, item: Versioned): void {
    if (item.id === undefined || !item.version) {
      return;
    }
    const key = this.key(kind, item.id, item.version);
    this.copies.delete(key);
    this.copies.set(key, JSON.parse(JSON.stringify(WidgetSaveVersions.ownContent(kind, item))));
    if (this.copies.size > MAX_COPIES) {
      this.copies.delete(this.copies.keys().next().value);
    }
  }

  // Loaded at a version this tab's saves replaced: an undo or a restore turned it back, so that version is current.
  private loaded(kind: VersionedKind, copy: Versioned): void {
    const item = `${kind}:${copy.id}`;
    const newest = this.newest.get(item);
    if (newest && copy.version !== newest.version && newest.replaced.has(copy.version)) {
      this.newest.delete(item);
    }
  }

  private key(kind: VersionedKind, id: number, version: string): string {
    return `${kind}:${id}:${version}`;
  }
}
